import { motion } from 'framer-motion';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Droplets,
  Calendar,
  Download,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';

const waterUsageData = [
  { month: 'Ene', consumo: 4500, ahorro: 1200 },
  { month: 'Feb', consumo: 3800, ahorro: 1400 },
  { month: 'Mar', consumo: 5200, ahorro: 1800 },
  { month: 'Abr', consumo: 6100, ahorro: 2200 },
  { month: 'May', consumo: 7500, ahorro: 2800 },
  { month: 'Jun', consumo: 8200, ahorro: 3100 },
];

const irrigationFrequency = [
  { day: 'Lun', riegos: 4 },
  { day: 'Mar', riegos: 3 },
  { day: 'Mié', riegos: 5 },
  { day: 'Jue', riegos: 4 },
  { day: 'Vie', riegos: 6 },
  { day: 'Sáb', riegos: 2 },
  { day: 'Dom', riegos: 1 },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

export default function Analytics() {
  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground">
            Analíticas
          </h1>
          <p className="text-muted-foreground mt-1">
            Estadísticas y métricas del sistema de riego
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2">
            <Calendar className="w-4 h-4" />
            Último mes
          </Button>
          <Button variant="outline" className="gap-2">
            <Download className="w-4 h-4" />
            Exportar
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Consumo total</p>
              <p className="text-2xl font-display font-bold">35.3 m³</p>
              <div className="flex items-center gap-1 text-xs text-destructive mt-1">
                <TrendingUp className="w-3 h-3" />
                <span>+12% vs mes anterior</span>
              </div>
            </div>
            <div className="p-3 rounded-xl bg-accent/10">
              <Droplets className="w-6 h-6 text-accent" />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Agua ahorrada</p>
              <p className="text-2xl font-display font-bold">12.5 m³</p>
              <div className="flex items-center gap-1 text-xs text-success mt-1">
                <TrendingUp className="w-3 h-3" />
                <span>+8% vs mes anterior</span>
              </div>
            </div>
            <div className="p-3 rounded-xl bg-success/10">
              <TrendingDown className="w-6 h-6 text-success" />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Riegos ejecutados</p>
              <p className="text-2xl font-display font-bold">156</p>
              <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                <span>Este mes</span>
              </div>
            </div>
            <div className="p-3 rounded-xl bg-primary/10">
              <BarChart3 className="w-6 h-6 text-primary" />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Eficiencia</p>
              <p className="text-2xl font-display font-bold">87%</p>
              <div className="flex items-center gap-1 text-xs text-success mt-1">
                <TrendingUp className="w-3 h-3" />
                <span>+3% vs mes anterior</span>
              </div>
            </div>
            <div className="p-3 rounded-xl bg-warning/10">
              <TrendingUp className="w-6 h-6 text-warning" />
            </div>
          </div>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Water usage chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-display">
              Consumo vs Ahorro de Agua
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={waterUsageData}>
                  <defs>
                    <linearGradient
                      id="consumoGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="hsl(200, 85%, 50%)"
                        stopOpacity={0.3}
                      />
                      <stop
                        offset="95%"
                        stopColor="hsl(200, 85%, 50%)"
                        stopOpacity={0}
                      />
                    </linearGradient>
                    <linearGradient
                      id="ahorroGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="hsl(142, 72%, 42%)"
                        stopOpacity={0.3}
                      />
                      <stop
                        offset="95%"
                        stopColor="hsl(142, 72%, 42%)"
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(214, 20%, 88%)"
                  />
                  <XAxis
                    dataKey="month"
                    stroke="hsl(215, 15%, 45%)"
                    fontSize={12}
                  />
                  <YAxis
                    stroke="hsl(215, 15%, 45%)"
                    fontSize={12}
                    tickFormatter={(value) => `${value}L`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(0, 0%, 100%)',
                      border: '1px solid hsl(214, 20%, 88%)',
                      borderRadius: '8px',
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="consumo"
                    name="Consumo (L)"
                    stroke="hsl(200, 85%, 50%)"
                    fill="url(#consumoGradient)"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="ahorro"
                    name="Ahorro (L)"
                    stroke="hsl(142, 72%, 42%)"
                    fill="url(#ahorroGradient)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            {/* Custom legend */}
            <div className="flex justify-center gap-6 mt-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[hsl(200,85%,50%)]" />
                <span className="text-sm text-muted-foreground">
                  Consumo (L)
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[hsl(142,72%,42%)]" />
                <span className="text-sm text-muted-foreground">
                  Ahorro (L)
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Irrigation frequency */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-display">
              Frecuencia de Riegos por Día
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={irrigationFrequency}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(214, 20%, 88%)"
                  />
                  <XAxis
                    dataKey="day"
                    stroke="hsl(215, 15%, 45%)"
                    fontSize={12}
                  />
                  <YAxis stroke="hsl(215, 15%, 45%)" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(0, 0%, 100%)',
                      border: '1px solid hsl(214, 20%, 88%)',
                      borderRadius: '8px',
                    }}
                  />
                  <Bar
                    dataKey="riegos"
                    name="Riegos"
                    fill="hsl(152, 45%, 28%)"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Additional info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-display">
            Resumen de Eficiencia
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 rounded-xl bg-muted/50">
              <p className="text-3xl font-display font-bold text-primary">
                26%
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Reducción de consumo vs riego manual
              </p>
            </div>
            <div className="text-center p-4 rounded-xl bg-muted/50">
              <p className="text-3xl font-display font-bold text-success">
                €420
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Ahorro estimado este mes
              </p>
            </div>
            <div className="text-center p-4 rounded-xl bg-muted/50">
              <p className="text-3xl font-display font-bold text-accent">95%</p>
              <p className="text-sm text-muted-foreground mt-1">
                Uptime del sistema
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
